import { createAsyncThunk } from '@reduxjs/toolkit';

import { adaptProductPublicAttributesToStore } from 'src/adapters/serviceToStore/adaptProductPublicAttributesToStore';
import type { UserState } from 'src/redux/user/slice';
import { fetchProductDetails } from 'src/services/cat-service-api/fetchProductDetails';
import type { CatServiceAPIResponseBody } from 'src/services/cat-service-api/types/CatServiceAPIResponseBody';
import { fetchListingsControlsConfig } from 'src/services/contentstack/fetchListingsControlsConfig';
import { fetchUnifiedProduct } from 'src/services/unified-product-service/fetchUnifiedProduct';
import { logger } from 'src/utils/serverLogger';
import type { RequestProductDetailsPayload } from './types';

interface RequestProductsOptions {
  cookie?: string;
  healthcheck?: boolean;
  previewing?: boolean;
  productURI: string;
}

interface MinimalState {
  user: UserState;
}

export const requestProductDetails = createAsyncThunk<
  RequestProductDetailsPayload,
  RequestProductsOptions
>(
  'product/requestProductDetails',
  async ({ cookie, healthcheck, previewing, productURI }, { getState }) => {
    if (healthcheck) {
      // Dynamically import json file to prevent it being included on the client. Healthcheck request only happens server side.
      const { healthCheckProductDetailsResponseBody } = await import(
        'src/services/cat-service-api/fixtures/healthCheckProductDetailsResponseBody'
      );
      const {
        metaDescription,
        metaRobotsIndexable,
        navigation,
        product: { actualCommissionRate, ...restOfProductData },
      } = healthCheckProductDetailsResponseBody;

      return {
        data: {
          product: restOfProductData,
          navigation,
          metaDescription,
          metaRobotsIndexable,
        } as Omit<CatServiceAPIResponseBody, 'actualCommissionRate'>,
      };
    }

    const {
      user: { currencyCode, deliveryZone },
    } = getState() as MinimalState;

    const [{ data, redirectUrl }, unifiedProduct, listingsControlsConfig] = await Promise.all([
      fetchProductDetails(productURI, {
        cookie,
        currencyCode,
        deliveryZone,
        previewing,
      }),
      fetchUnifiedProduct(productURI, { timeout: 750 }).catch((error) => {
        logger.warn(`Failed to fetch unified product for ${productURI}: ${error.message}`);
        return null;
      }),
      fetchListingsControlsConfig(),
    ]);

    if (redirectUrl) {
      return { redirectUrl };
    }

    const publicAttributes = unifiedProduct
      ? adaptProductPublicAttributesToStore(
          unifiedProduct.attributes.allAttributes,
          listingsControlsConfig,
        )
      : [];

    const {
      metaDescription,
      metaRobotsIndexable,
      navigation,
      product: { actualCommissionRate, ...restOfProductData },
    } = data as CatServiceAPIResponseBody;

    return {
      attributes: unifiedProduct ? unifiedProduct.attributes.allAttributes : {},
      publicAttributes,
      data: {
        product: restOfProductData,
        navigation,
        metaDescription,
        metaRobotsIndexable: typeof metaRobotsIndexable === 'boolean' ? metaRobotsIndexable : true,
      } as Omit<CatServiceAPIResponseBody, 'actualCommissionRate'>,
    };
  },
);
